import { Alert, AlertDescription, AlertTitle } from "@/components/alert";
import { LoadingSpinner } from "@/components/loading-spinner";
import { Tabs, TabsList, TabsTrigger } from "@/components/tabs";
import { useBusinessMetrics } from "@/http/api-hooks";
import { ChartLine, Table } from "lucide-react";
import React, { useState } from "react";
import { BusinessMetricsCharts } from "./business-charts";
import { BusinessMetricsTable, BusinessSection } from "./business-tables";

type ViewMode = "table" | "graphs";

export const BusinessMetricsPage: React.FC = () => {
  const { data, isLoading, error } = useBusinessMetrics();
  const [viewMode, setViewMode] = useState<ViewMode>("table");

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>Failed to load business metrics: {error.message}</AlertDescription>
      </Alert>
    );
  }

  if (!data) {
    return <div>No data available</div>;
  }

  // Filter rows by category
  const evergreenRows = data.rows.filter(r => r.category === "Evergreen");
  const northStarRows = data.rows.filter(r => r.category === "North Star");
  const engineeringRows = data.rows.filter(r => r.category === "Engineering");
  const adminRows = data.rows.filter(r => r.category === "Admin");
  const derivedRows = data.rows.filter(r => r.category === "Derived");

  const sections: BusinessSection[] = [
    { title: "Evergreen Metrics", rows: evergreenRows },
    { title: "North Star Metrics", rows: northStarRows },
    { title: "Engineering Metrics", rows: engineeringRows },
    { title: "Admin / Financial", rows: adminRows },
    { title: "Derived Metrics (Martin)", rows: derivedRows },
  ];

  return (
    <>
      <div className="flex justify-start">
        <Tabs value={viewMode} onValueChange={v => setViewMode(v as ViewMode)}>
          <TabsList>
            <TabsTrigger value="table" className="gap-1.5">
              <Table className="h-4 w-4" />
              Table
            </TabsTrigger>
            <TabsTrigger value="graphs" className="gap-1.5">
              <ChartLine className="h-4 w-4" />
              Graphs
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="mt-8">
        {viewMode === "table" ? (
          <BusinessMetricsTable months={data.months} sections={sections} />
        ) : (
          <BusinessMetricsCharts months={data.months} sections={sections} />
        )}
      </div>
    </>
  );
};
